import React, { ReactText, useContext, useEffect, useState } from 'react';
import {
  formatters,
  buildPredeterminedReturnSeries,
  isPredeterminedSession,
} from 'services';
import { AreaChart } from 'components';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDisplaySeries,
  Metrics,
  Player,
  REAL_TIME_SCENARIO,
  selectors,
} from 'state';
import { Button, Card, Modal, Typography } from 'antd';
import { TrainerSessionContext } from '../TrainerSessionContext';
import { FullscreenOutlined } from '@ant-design/icons';

const PlayersEvolutionGraph: React.FC = () => {
  const session = useSelector(selectors.activeSession);
  const isRealTime = session.scenario_type === REAL_TIME_SCENARIO;
  const isPredetermined = isPredeterminedSession(session);
  // Benchmark configured (any kind)? Mirrors the backend `has_benchmark` gate
  // (analysis_functions.py): a constant, a session/individual composition, or a
  // portfolio composition. When absent, `market_index_return` is a flat 0 seed,
  // so we drop the Benchmark series instead of drawing a meaningless zero line.
  const hasBenchmark = Boolean(
    session.benchmark_constant ||
      session.benchmark_compositions?.length ||
      session.benchmark_portfolio_compositions?.length ||
      session.allow_individual_benchmark,
  );
  const trainerContext = useContext(TrainerSessionContext);
  const players = useSelector(selectors.playersList);
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();

  // Predetermined only: hydrate the existing display fills once (mirrors the Performance tab's
  // fetchMetrics gate); live gaps append via the display_series_filled socket event. Fixed/RT
  // sessions never dispatch this.
  useEffect(() => {
    if (!isPredetermined || session.display_series_fetched) return;
    dispatch(fetchDisplaySeries({ session_id: session.id }));
  }, [dispatch, isPredetermined, session.display_series_fetched, session.id]);

  const additionalDataSeries = trainerContext.selectedRows.map(playerId => {
    const player = players.find(player => player.id === playerId);
    let datapoints: { x: ReactText; y: number }[] | { x: number; y: number }[] =
      [{ x: 0, y: 0 }];
    if (player !== undefined) {
      if (isPredetermined) {
        // THIRD branch: knit canonical anchors + display fills onto an absolute datetime
        // axis (trainer surface). Fixed/RT mapping below stays byte-for-byte.
        datapoints = buildPredeterminedReturnSeries(player, session, 'trainer').portfolio;
      } else {
        datapoints = player.player_history.map((datapoint, index) => {
          if (!session.show_graph_as_rt) {
            return {
              x: isRealTime ? datapoint.timestamp : index * session.frequency + 1,
              y: datapoint.portfolio_return,
            };
          } else {
            return {
              x: datapoint.timestamp,
              y: datapoint.portfolio_return,
            };
          }
        });
      }
    }
    return {
      name: player ? player.name : 'undefined',
      data: datapoints,
    };
  });

  let metrics: Metrics[] = [];
  // the predetermined knit needs the source PLAYER (its display fills), not just its metrics
  let benchmarkSourcePlayer: Player | undefined;
  if (session.allow_individual_benchmark) {
    // INDIVIDUAL BENCHMARK CASE: metrics are taken from the only selected player because each player has its own metrics
    let player: Player | undefined;
    if (trainerContext.selectedRows.length > 0) {
      player = players.find(
        player => player.id === trainerContext.selectedRows[0]
      );
    }
    if (player !== undefined) {
      metrics = player.metrics;
      benchmarkSourcePlayer = player;
    }
  } else {
    // SESSION LEVEL BENCHMARK: all players carry the same benchmark, so we can
    // read it off any single player's metrics. We must source it from an ACTIVE
    // (has traded) non-busted player though: only those accounts receive live
    // `metrics_received` socket pushes (inactive accounts get their metrics
    // written to the DB but never broadcast). Sourcing from an inactive player
    // freezes the benchmark line until a page refresh re-fetches from the DB.
    const source_player =
      players.find(player => player.deals !== 0 && player.busted === false) ??
      players.find(player => player.busted === false) ??
      players[0];
    if (source_player !== undefined) {
      metrics = source_player.metrics;
      benchmarkSourcePlayer = source_player;
    }
  }
  let benchmarkData = metrics.map((item, index) => ({
    x: isRealTime ? item.timestamp : index * session.frequency + 1,
    y: item.market_index_return,
  }));

  if (session.show_graph_as_rt) {
    benchmarkData = metrics.map((item, index) => ({
      x: item.timestamp,
      y: item.market_index_return,
    }));
  }

  if (isPredetermined) {
    // THIRD branch: knitted benchmark (metric anchors + shared fill benchmark) on the
    // trainer's absolute datetime axis; fixed/RT mappings above stay byte-for-byte.
    benchmarkData = benchmarkSourcePlayer
      ? buildPredeterminedReturnSeries(benchmarkSourcePlayer, session, 'trainer').benchmark
      : [];
  }

  if (isRealTime) {
    // Delete all the first points with return 0
    // while (evolutionData.length && evolutionData[0].y === 0) {
    //     evolutionData.splice(0, 1);
    // }
    while (benchmarkData.length && benchmarkData[0].y === 0) {
      benchmarkData.splice(0, 1);
    }
  }
  // if (session.benchmark_constant !== 0) {
  //   benchmarkData[0].y = session.benchmark_constant;
  // }

  // AreaChart always renders its primary `data` as a named series. WITH a
  // benchmark, the primary IS the benchmark — unchanged from before. WITHOUT one,
  // promote the first selected player into the primary slot so no (empty)
  // "Benchmark" series/legend entry is drawn; the rest stay as additional lines.
  const primaryName = hasBenchmark
    ? 'Benchmark'
    : (additionalDataSeries[0]?.name ?? '');
  const primaryData = hasBenchmark
    ? benchmarkData
    : (additionalDataSeries[0]?.data ?? []);
  const extraLines = hasBenchmark
    ? additionalDataSeries
    : additionalDataSeries.slice(1);

  // predetermined joins RT on the datetime default (xaxis undefined -> AreaChart 'datetime');
  // fixed keeps the numeric "Day N" config byte-for-byte
  const xaxis =
    isRealTime || isPredetermined
      ? undefined
      : {
          tooltip: {
            enabled: false,
          },
          labels: {
            formatter: function (value: unknown) {
              return `Day ${value}`;
            },
            show: false,
          },
        };

  const chart = (
    <div>
      <Typography.Title level={3}>
        Benchmark & Players Evolution
      </Typography.Title>
      <Card>
        <AreaChart
          title=""
          dataSeriesName={primaryName}
          data={primaryData}
          yaxisFormatter={formatters.percent1Digit}
          xaxis={xaxis}
          tooltipDateFormat={'d MMM HH:mm'}
          additionalLines={extraLines}
        />
        {!expanded && (
          <a
            style={{ position: 'absolute', top: 20, right: 20 }}
            onClick={() => setExpanded(true)}
          >
            <FullscreenOutlined />
          </a>
        )}
      </Card>
    </div>
  );

  return (
    <div>
      {expanded ? (
        <Modal
          width={1000}
          open={expanded}
          onCancel={() => setExpanded(false)}
          footer={[]}
        >
          {chart}
        </Modal>
      ) : (
        <div style={{ width: 471 }}>
          {/*<Typography.Title level={3}>*/}
          {/*  Benchmark & Players Evolution*/}
          {/*</Typography.Title>*/}
          {/*<Card>*/}
          {/*  <AreaChart*/}
          {/*    title=""*/}
          {/*    dataSeriesName="Benchmark"*/}
          {/*    data={benchmarkData}*/}
          {/*    yaxisFormatter={formatters.percent1Digit}*/}
          {/*    xaxis={xaxis}*/}
          {/*    tooltipDateFormat={'d MMM HH:mm'}*/}
          {/*    additionalLines={additionalDataSeries}*/}
          {/*  />*/}
          {/*</Card>*/}
          {chart}
        </div>
      )}
    </div>
  );
};

export default PlayersEvolutionGraph;
